// src/components/layout/Layout.js
import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, TrendingUp, TrendingDown, Briefcase,
  Database, Users, Settings, ChevronDown, ChevronRight,
  LogOut, Menu, X, BarChart3, History
} from 'lucide-react';

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { label: 'Purchase', icon: TrendingUp, path: '/purchase' },
  { label: 'Sell', icon: TrendingDown, path: '/sell' },
  { label: 'Portfolio', icon: Briefcase, path: '/portfolio' },
  {
    label: 'Masters', icon: Database, children: [
      { label: 'Script Master', path: '/masters/scripts' },
      { label: 'Customer Master', path: '/masters/customers', icon: Users },
      { label: 'Brokerage Master', path: '/masters/brokerage', icon: Settings },
      { label: 'Price History', path: '/masters/price-history', icon: History },
    ]
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mastersOpen, setMastersOpen] = useState(true);

  const handleLogout = () => { logout(); navigate('/login'); };
  const isActive = (path) => location.pathname === path;
  const isMasterActive = () => location.pathname.startsWith('/masters');

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f4f8', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarOpen ? 240 : 64, transition: 'width 0.3s ease',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        display: 'flex', flexDirection: 'column', position: 'fixed',
        top: 0, left: 0, height: '100vh', zIndex: 100, overflow: 'hidden'
      }}>
        {/* Logo */}
        <div style={{ padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <BarChart3 size={20} color="#fff" />
          </div>
          {sidebarOpen && <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, letterSpacing: '-0.3px' }}>InvestTrack</div>
            <div style={{ color: '#94a3b8', fontSize: 10, letterSpacing: '0.5px', textTransform: 'uppercase' }}>BSE · NSE</div>
          </div>}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
          {navItems.map((item) => {
            if (item.children) {
              return (
                <div key={item.label}>
                  <button
                    onClick={() => setMastersOpen(!mastersOpen)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px',
                      borderRadius: 8, border: 'none', cursor: 'pointer', transition: 'background 0.15s',
                      background: isMasterActive() ? 'rgba(59,130,246,0.15)' : 'transparent',
                      color: isMasterActive() ? '#93c5fd' : '#94a3b8'
                    }}
                  >
                    <item.icon size={18} style={{ flexShrink: 0 }} />
                    {sidebarOpen && <>
                      <span style={{ flex: 1, textAlign: 'left', fontSize: 14, fontWeight: 500 }}>{item.label}</span>
                      {mastersOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </>}
                  </button>
                  {sidebarOpen && mastersOpen && item.children.map(child => (
                    <Link key={child.path} to={child.path} style={{ textDecoration: 'none' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px 8px 38px',
                        borderRadius: 8, margin: '1px 0', fontSize: 13, transition: 'background 0.15s',
                        background: isActive(child.path) ? 'rgba(59,130,246,0.2)' : 'transparent',
                        color: isActive(child.path) ? '#93c5fd' : '#64748b',
                        borderLeft: isActive(child.path) ? '2px solid #3b82f6' : '2px solid transparent'
                      }}>
                        {child.label}
                      </div>
                    </Link>
                  ))}
                </div>
              );
            }
            return (
              <Link key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px',
                  borderRadius: 8, margin: '2px 0', transition: 'background 0.15s',
                  background: isActive(item.path) ? 'rgba(59,130,246,0.2)' : 'transparent',
                  color: isActive(item.path) ? '#93c5fd' : '#94a3b8',
                  borderLeft: isActive(item.path) ? '2px solid #3b82f6' : '2px solid transparent'
                }}>
                  <item.icon size={18} style={{ flexShrink: 0 }} />
                  {sidebarOpen && <span style={{ fontSize: 14, fontWeight: isActive(item.path) ? 600 : 400 }}>{item.label}</span>}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* User */}
        {sidebarOpen && <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>{user?.name}</div>
          <div style={{ color: '#64748b', fontSize: 11, marginBottom: 8 }}>{user?.role?.toUpperCase()}</div>
          <button onClick={handleLogout} style={{
            display: 'flex', alignItems: 'center', gap: 8, color: '#ef4444', background: 'none',
            border: 'none', cursor: 'pointer', fontSize: 13, padding: 0
          }}>
            <LogOut size={14} /> Sign out
          </button>
        </div>}
      </aside>

      {/* Main */}
      <div style={{ marginLeft: sidebarOpen ? 240 : 64, flex: 1, transition: 'margin-left 0.3s ease' }}>
        {/* Top bar */}
        <header style={{
          background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 24px',
          height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 50
        }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#64748b' }}>
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 13, fontWeight: 700
            }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{user?.name}</span>
          </div>
        </header>

        <main style={{ padding: 24 }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
